import { createContext, FC, PropsWithChildren, useContext } from 'react';
import {
    OptionsObject,
    SnackbarKey,
    SnackbarMessage,
    SnackbarProvider,
    VariantType,
    useSnackbar
} from 'notistack';
import { CrossThin, Box } from 'components';
import styled from 'styled-components';

export interface INotificationContext {
    info(message: SnackbarMessage, options?: OptionsObject): SnackbarKey;
    success(message: SnackbarMessage, options?: OptionsObject): SnackbarKey;
    error(message: SnackbarMessage, options?: OptionsObject): SnackbarKey;
    warning(message: SnackbarMessage, options?: OptionsObject): SnackbarKey;
    general(message: SnackbarMessage, options?: OptionsObject): SnackbarKey;
    closeNotification(key: SnackbarKey): void;
}

const NotificationContext = createContext<INotificationContext>(null);
const useNotification = () =>
    useContext<INotificationContext>(NotificationContext);

const NotificationProvider: FC<PropsWithChildren> = ({ children }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const _createSnack = (
        type: VariantType,
        message: SnackbarMessage,
        options?: OptionsObject
    ) => {
        return enqueueSnackbar(
            message,
            Object.assign(
                {
                    variant: type
                },
                options ?? {}
            )
        );
    };

    const snacks = {
        info: (message: SnackbarMessage, options?: OptionsObject) =>
            _createSnack('info', message, options),
        success: (message: SnackbarMessage, options?: OptionsObject) =>
            _createSnack('success', message, options),
        error: (message: SnackbarMessage, options?: OptionsObject) => {
            return _createSnack(
                'error',
                message,
                Object.assign(
                    {
                        action: (key: SnackbarKey) => (
                            <Box
                                onClick={() => closeSnackbar(key)}
                                mx="2"
                                style={{
                                    cursor: 'pointer'
                                }}>
                                <CrossThin color="background" />
                            </Box>
                        ),
                        persist: true
                    },
                    options
                )
            );
        },
        warning: (message: SnackbarMessage, options?: OptionsObject) =>
            _createSnack('warning', message, options),
        general: (message: SnackbarMessage, options?: OptionsObject) =>
            _createSnack(
                'default',
                message,
                Object.assign(
                    {
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'center'
                        },
                        preventDuplicate: true
                    },
                    options
                )
            )
    };

    return (
        <NotificationContext.Provider
            value={{ ...snacks, closeNotification: closeSnackbar }}>
            {children}
        </NotificationContext.Provider>
    );
};

const PREFIX = 'notifications-override' as const;
const classes = {
    success: `${PREFIX}-success`,
    error: `${PREFIX}-error`,
    warning: `${PREFIX}-warning`,
    info: `${PREFIX}-info`,
    default: `${PREFIX}-default`
} as const;

const SnackbarProviderWithClasses = styled.div`
    & .${classes.info} {
        background-color: ${({ theme }) =>
            theme.palette.primary.dark} !important;
    }
`;

const NotificationManager: FC<PropsWithChildren> = ({ children }) => {
    return (
        <SnackbarProviderWithClasses>
            <SnackbarProvider
                classes={{
                    variantInfo: classes.info
                }}
                maxSnack={3}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <NotificationProvider>{children}</NotificationProvider>
            </SnackbarProvider>
        </SnackbarProviderWithClasses>
    );
};

export { NotificationManager, useNotification };
